import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePractice } from '../context/PracticeContext';

export default function UserProfile() {
  const { user, refreshUser } = useAuth();
  const { selectedPractice } = usePractice();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordData, setPasswordData] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const loadMfaStatus = useCallback(async () => {
    try {
      const res = await api.request('/auth/mfa/status');
      setMfaStatus(res.data);
    } catch { setMfaStatus(null); }
  }, []);

  useEffect(() => { loadMfaStatus(); }, [loadMfaStatus]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.request('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(formData),
      });
      setMessage('Profile updated');
      setEditing(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      setMessage('Error: ' + (err.message || 'Failed to save'));
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPass !== passwordData.confirm) {
      setMessage('Error: New passwords do not match');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    if (passwordData.newPass.length < 8) {
      setMessage('Error: Password must be at least 8 characters');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    try {
      setChangingPassword(true);
      await api.request('/auth/password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: passwordData.current,
          newPassword: passwordData.newPass,
        }),
      });
      setMessage('Password changed successfully');
      setPasswordData({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      setMessage('Error: ' + (err.message || 'Failed to change password'));
    } finally {
      setChangingPassword(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (!user) return null;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Profile Settings</h2>

      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: message.startsWith('Error') ? '#FEE2E2' : '#ECFDF5',
          color: message.startsWith('Error') ? '#DC2626' : '#059669',
        }}>
          {message}
        </div>
      )}

      {/* User Info */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={styles.sectionTitle}>Personal Information</h3>
          {!editing && (
            <button onClick={() => setEditing(true)} style={styles.editBtn}>Edit</button>
          )}
        </div>
        {editing ? (
          <div>
            {[
              { key: 'firstName', label: 'First Name' },
              { key: 'lastName', label: 'Last Name' },
              { key: 'email', label: 'Email' },
              { key: 'phone', label: 'Phone' },
            ].map(f => (
              <div key={f.key} style={styles.fieldRow}>
                <label style={styles.label}>{f.label}</label>
                <input
                  value={formData[f.key] || ''}
                  onChange={e => setFormData({ ...formData, [f.key]: e.target.value })}
                  style={styles.input}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} style={styles.cancelBtn}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <InfoRow label="Name" value={`${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`} />
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={user.phone || 'Not set'} />
            <InfoRow label="Role" value={(user.role || '').replace('_', ' ')} />
            <InfoRow label="Practice" value={selectedPractice?.name || 'N/A'} />
          </div>
        )}
      </div>

      {/* Change Password */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Change Password</h3>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Current Password</label>
          <input type="password" value={passwordData.current}
            onChange={e => setPasswordData({ ...passwordData, current: e.target.value })}
            style={styles.input} />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>New Password</label>
          <input type="password" value={passwordData.newPass}
            onChange={e => setPasswordData({ ...passwordData, newPass: e.target.value })}
            style={styles.input} />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Confirm New Password</label>
          <input type="password" value={passwordData.confirm}
            onChange={e => setPasswordData({ ...passwordData, confirm: e.target.value })}
            style={styles.input} />
        </div>
        <button onClick={handlePasswordChange} disabled={changingPassword || !passwordData.current || !passwordData.newPass}
          style={{ ...styles.saveBtn, marginTop: 8, opacity: (!passwordData.current || !passwordData.newPass) ? 0.5 : 1 }}>
          {changingPassword ? 'Changing...' : 'Change Password'}
        </button>
      </div>

      {/* MFA Status */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Two-Factor Authentication</h3>
        {mfaStatus?.enabled ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#059669', fontWeight: 600, fontSize: 13 }}>Enabled</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>— Authenticator app configured</span>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              MFA is not enabled. Enable it for additional security.
            </p>
            <button
              onClick={() => window.location.href = '/practice-view/practice-settings?tab=general'}
              style={styles.editBtn}
            >
              Enable MFA
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 140, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#111827' }}>{value}</span>
    </div>
  );
}

const styles = {
  container: { maxWidth: 640, margin: '0 auto', padding: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 20 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  label: { width: 160, fontSize: 13, color: '#374151', fontWeight: 500 },
  input: { flex: 1, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 },
  editBtn: { padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151' },
  saveBtn: { padding: '8px 20px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  cancelBtn: { padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151' },
};
